import {
  AddIcon,
  Button,
  Chip,
  DotsHorizontalIcon,
  Dropdown,
  DropdownMenu,
  DropdownMenuItem,
  Grid,
  GridColumn,
  GridRow,
  GroupsIcon,
  ImportIcon,
  LockIcon,
  Row,
  showToast,
  Table,
  TableColumns,
  Tooltip,
  UnlockIcon,
  useDefaultSelecting,
  VisibleIcon,
} from '@bp/ui-components';
import { observer } from 'mobx-react-lite';
import { PropsWithChildren, useCallback, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AppLayoutContext } from '../../../contexts/AppLayoutContextProvider';
import { useAuthClaims } from '../../../hooks/useAuthClaims';
import { useColumnsSort } from '../../../hooks/useColumnsSort';
import { useConfirm } from '../../../hooks/useConfirm';
import { useHiddenColumns } from '../../../hooks/useHiddenColumns';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { useMemorizedCacheTags } from '../../../hooks/useMemorizedCacheTag';
import { usePermissionChecker } from '../../../hooks/usePermissionChecker';
import { useSelectedSchoolYear } from '../../../hooks/useSelectedSchoolYear';
import { LessonTabFilterType } from '../../../pages/Deputate/Plan/Lesson/LessonsPage';
import {
  InheritLessonTeachersType,
  LessonEditStatus,
  LessonUuidsWithCardsDocument,
  LessonUuidsWithCardsQuery,
  LessonUuidsWithCardsQueryVariables,
  useInheritLessonTeachersMutation,
  useMergeLessonsMutation,
} from '../../../types/planung-graphql-client-defs';
import { partition } from '../../../utils/arrayFunc';
import { showSuccessUpdateToast, showUserErrorToast } from '../../../utils/toast';
import { onlyUnique } from '../../../utils/unique-filter';
import { urqlClient } from '../../../utils/urqlClient';
import { TransferCurriculaModal } from '../../Curricula/modals/TransferCurriculaModal';
import { LessonCard } from '../../LessonCard/LessonCard';
import { EditStatusTooltipContent } from '../../TooltipContents/EditStatusTooltipContent/EditStatusTooltipContent';
import { useDeputateLessons } from '../hooks/useDeputateLessons';
import { DeputateEpochModal } from '../modals/DeputateEpochModal';
import { DeputateLessonModal } from '../modals/DeputateLessonModal';
import { MergeSubjectContainerLessonsModal } from '../modals/MergeSubjectContainerLessonsModal/MergeSubjectContainerLessonsModal';
import { LessonTableType } from '../TimetableVersion/Table/TimetableVersionLessonsTable';
import { LessonFormType } from '../types';
import { estimatedRowHeightByCards } from '../utils/estimatedRowHeight';
import style from './DeputateLessonsTable.module.scss';

type LessonOverviewTableProps = PropsWithChildren & {
  classUuid?: string | undefined;
  teacherUuid?: string | undefined;
  lessonsData: LessonTableType[];
  lessonTabFilterType?: LessonTabFilterType;
  configKey: string;
  defaultInitialValues?: Partial<LessonFormType>;
  showTransferCurricula?: boolean;
};

export const DeputateLessonsTable = observer(
  ({
    classUuid,
    lessonsData,
    lessonTabFilterType,
    configKey,
    defaultInitialValues,
    teacherUuid,
    showTransferCurricula = false,
  }: LessonOverviewTableProps) => {
    const { t } = useTranslation();
    const isMobile = useIsMobile();
    const { pimAuthClaims } = useAuthClaims();

    const { scrollRef } = useContext(AppLayoutContext);

    const schoolYear = useSelectedSchoolYear();
    const context = useMemorizedCacheTags(['LESSONS', 'LESSON_CLASS']);
    const permissionChecker = usePermissionChecker();
    const readonly = !permissionChecker?.canWriteDeputate();

    const { columnVisibility, saveColumnVisibility } = useHiddenColumns(configKey, {
      'lesson.groups': true,
      classes: false,
      groupNames: false,
    });
    const { sorting, saveSorting } = useColumnsSort(configKey);
    const { rowSelection, onRowSelectionChange, clearSelection } = useDefaultSelecting();

    const [loading, setLoading] = useState(false);
    const [lessonUuid, setLessonUuid] = useState<string | null>(null);
    const [editModalOpen, setEditModalOpen] = useState<boolean>(false);
    const [epochModalOpen, setEpochModalOpen] = useState<boolean>(false);
    const [transferModalOpen, setTransferModalOpen] = useState<boolean>(false);

    const [lessonsToMerge, setLessonsToMerge] = useState<LessonTableType[]>([]);

    const [mergeSubjectContainerLessonsModalOpen, setMergeSubjectContainerLessonsModalOpen] = useState<boolean>(false);

    const { confirm, ConfirmationDialog } = useConfirm();

    const {
      detachLessons,
      assignLessons,
      duplicateLesson,
      deleteLessons,
      updateCheckboxes: update,
      loadingState,
      lessons: allLessons,
      teachers: allTeachers,
    } = useDeputateLessons({
      organizationUuid: pimAuthClaims.getOrganizationUuid(),
      schoolYearUuid: schoolYear?.uuid ?? '',
    });

    const [, mergeLessons] = useMergeLessonsMutation();

    const updateCheckboxes = useCallback(
      async ({
        type,
        uuids,
        value,
      }: {
        type: 'teachingLoadEnabled' | 'timetableEnabled' | 'elective';
        uuids: string[];
        value: boolean;
      }) => {
        await update({
          type,
          uuids,
          value,
        });
        onRowSelectionChange({});
      },
      [onRowSelectionChange, update],
    );

    const handleAssign = useCallback(
      async (item: LessonTableType, teachers: { uuid: string }[]) => {
        const curriculumPersonProperties = allLessons.find((lesson) => lesson.uuid === item.uuid)?.item
          .curriculumPersons[0];

        await assignLessons([item.uuid], teachers, curriculumPersonProperties);
      },
      [allLessons, assignLessons],
    );
    // biome-ignore lint/correctness/useExhaustiveDependencies(t): ignore t
    const handleDetach = useCallback(
      async (uuids: string[]) => {
        const response = await detachLessons(uuids);

        if (response.error) {
          showToast(t('lesson.message.detach.error'), { type: 'error' });
        } else {
          showToast(t('lesson.message.detach.success'), { type: 'success' });
        }
      },
      [detachLessons],
    );

    const handleAddLesson = () => {
      setEditModalOpen(true);
    };

    const handleAddSubjectContainerLesson = () => {
      setEpochModalOpen(true);
    };

    const handleAddEpochClose = () => {
      setLessonUuid(null);
      setEpochModalOpen(false);
    };

    const handleClose = () => {
      setLessonUuid(null);
      setEditModalOpen(false);
    };

    const handleDelete = useCallback(
      async (lessons: LessonTableType[]) => {
        const usedInTimetableVersionData = await urqlClient
          .query<LessonUuidsWithCardsQuery, LessonUuidsWithCardsQueryVariables>(
            LessonUuidsWithCardsDocument,
            {
              uuids: lessons.map((l) => l.uuid),
            },
            { requestPolicy: 'network-only' },
          )
          .toPromise();

        const usedInTimetableVersionUuids = usedInTimetableVersionData.data?.lessonUuidsWithCards ?? [];
        const [canBeDeleted, canNotDelete] = partition(lessons, (l) => !usedInTimetableVersionUuids.includes(l.uuid));

        const message = (
          <Grid>
            <GridRow headline={t('lesson.confirm.delete.default', { count: canBeDeleted.length })}>
              <GridColumn width={12}>
                {canBeDeleted.map((l) => {
                  return (
                    <GridRow key={l.uuid} spacingTop={'s'} spacingBottom={'s'}>
                      <GridColumn width={4}>{l.subject}</GridColumn>
                      <GridColumn width={4}>{l.groupClassNames}</GridColumn>
                      <GridColumn width={4}>{l.teachers.map((t) => t.name).join(', ')}</GridColumn>
                    </GridRow>
                  );
                })}
              </GridColumn>
            </GridRow>
            {canNotDelete.length > 0 && (
              <GridRow headline={t('lesson.confirm.delete.withIgnored', { count: canNotDelete.length })}>
                <GridColumn width={12}>
                  {canNotDelete.map((l) => {
                    return (
                      <GridRow key={l.uuid} spacingTop={'s'} spacingBottom={'s'}>
                        <GridColumn width={4}>{l.subject}</GridColumn>
                        <GridColumn width={4}>{l.groupClassNames}</GridColumn>
                        <GridColumn width={4}>{l.teachers.map((t) => t.name).join(', ')}</GridColumn>
                      </GridRow>
                    );
                  })}
                </GridColumn>
              </GridRow>
            )}
          </Grid>
        );

        const confirmed = await confirm({ message: message });
        if (confirmed) {
          const response = await deleteLessons(canBeDeleted.map((l) => l.uuid));
          if (response.error) {
            showToast(t('lesson.message.delete.error'), { type: 'error' });
          } else {
            showToast(t('lesson.message.delete.success'), { type: 'success' });
          }
        }
      },
      [confirm, deleteLessons, t],
    );

    const handleDuplicate = useCallback(
      async (uuids: string[]) => {
        if (loadingState.some(({ uuid, loading }) => loading && uuids.includes(uuid))) {
          return;
        }
        const response = await duplicateLesson(uuids);
        if (response) {
          if (response.some((r) => r.error)) {
            showToast(t('lesson.message.duplicate.error'), { type: 'error' });
          } else {
            showToast(t('lesson.message.duplicate.success'), { type: 'success' });
          }
        } else {
          showToast(t('lesson.message.duplicate.error'), { type: 'error' });
        }
      },
      [duplicateLesson, loadingState, t],
    );

    const [, inheritLessonTeachers] = useInheritLessonTeachersMutation();

    const handleInheritLessonTeachers = useCallback(
      async (lessonUuids: string[], type: InheritLessonTeachersType) => {
        setLoading(true);
        const res = await inheritLessonTeachers(
          {
            lessonUuids,
            type,
          },
          context,
        );
        setLoading(false);

        if (res.error) {
          showUserErrorToast({ error: res.error });
        } else {
          showSuccessUpdateToast();
        }
      },
      [inheritLessonTeachers, context],
    );

    const getChipBgColor = (editStatus: LessonTableType['editStatus']) => {
      switch (editStatus) {
        case LessonEditStatus.Blocked:
          return 'var(--color-error)';
        case LessonEditStatus.PlacedCards:
        case LessonEditStatus.InVersion:
          return 'var(--color-primary-lighter)';
        case LessonEditStatus.Unused:
        default:
          return 'var(--color-grey-lighter)';
      }
    };

    const getChipIconColor = (editStatus: LessonTableType['editStatus']) => {
      switch (editStatus) {
        case LessonEditStatus.Blocked:
          return 'var(--chip-active-color)';
        case LessonEditStatus.PlacedCards:
        case LessonEditStatus.InVersion:
          return 'var(--chip-color)';
        default:
          return 'var(--chip-color)';
      }
    };

    const columns: TableColumns<LessonTableType>[] = [
      {
        header: t('lesson.table.subject'),
        id: 'subject',
        size: 200,
        accessorKey: 'subject',
      },
      {
        header: t('common.type'),
        id: 'type',
        isMultiline: true,
        enableSorting: true,
        enableGlobalFilter: false,
        cell: ({ row }) => {
          if (row.original.isSubjectContainerLesson) {
            return t('epochs.plan');
          }
          const cardInfos = row.original.cardsInfos;
          const allCards = [...(cardInfos?.defaultCards ?? []), ...(cardInfos?.subjectContainerCards ?? [])];

          return (
            <div>
              {allCards.length > 0 && (
                <>
                  {allCards.map((group) => {
                    const card = group[0];
                    return (
                      <div key={card.uuid} title={card.typeLabel}>
                        {card.typeLabelShort}
                      </div>
                    );
                  })}
                </>
              )}
            </div>
          );
        },
        accessorFn: ({ cardsInfos, uuid, isSubjectContainerLesson }) => {
          const allCards = [...(cardsInfos?.defaultCards ?? []), ...(cardsInfos?.subjectContainerCards ?? [])];
          const sum = allCards.length;
          let sortValue = isSubjectContainerLesson ? `0000_${sum}_` : `${sum}_`;

          if (allCards.length > 0) {
            sortValue += allCards.map((dc) => dc.map((c) => c.typeLabelShort).join(',')).join(',');
          }
          return sortValue;
        },
        size: 90,
      },
      {
        header: t('common.hours'),
        id: 'cards',
        size: 150,
        isMultiline: true,
        enableSorting: false,
        enableGlobalFilter: false,
        enableColumnFilter: false,
        canOverflow: true,
        cell: ({ row }) => {
          const cardInfos = row.original.cardsInfos;
          const allCards = [...(cardInfos?.defaultCards ?? []), ...(cardInfos?.subjectContainerCards ?? [])];
          return (
            <div className={style['card-position']}>
              {allCards.map((group) => {
                const card = group[0];
                return (
                  <div key={card.uuid} title={card.cardLabel}>
                    <LessonCard
                      label={card.cardLabelShort}
                      lessonClasses={card.lesson}
                      count={card.counter}
                      teacherColors={card.colors}
                      duration={card.duration}
                      showHover={false}
                    />
                  </div>
                );
              })}
            </div>
          );
        },
      },
      {
        header: t('lesson.table.classesGroups'),
        id: 'groupClassNames',
        accessorKey: 'groupClassNames',
        size: 125,
        cell: ({ row }) => {
          return <Tooltip content={row.original.groupClassNames}>{row.original.groupClassNames}</Tooltip>;
        },
      },
      {
        header: t('lesson.table.classes'),
        id: 'classes',
        size: 125,
        cell: ({ row }) => {
          const shortNames = row.original.classes.map((node) => node.name).join(', ');
          return <Tooltip content={shortNames}>{shortNames}</Tooltip>;
        },
        accessorFn: (data) => {
          const shortNames = data.classes.map((node) => node.name);
          return shortNames.join(', ');
        },
      },
      {
        header: t('lesson.table.groups'),
        id: 'groupNames',
        size: 125,
        cell: ({ row }) => {
          const groups = row.original.classes
            .flatMap((node) => node.groups)
            .map((group) => group?.shortName ?? '')
            .filter(onlyUnique)
            .join(', ');
          return <Tooltip content={groups}>{groups}</Tooltip>;
        },
        accessorFn: (data) => {
          const groups = data.classes.flatMap((node) => node.groups);
          return groups.map((group) => group?.shortName ?? '').join(', ');
        },
      },
      {
        header: t('lesson.table.teacher'),
        id: 'teachers',
        cell: ({ row }) => {
          const displayNamesShort = row.original.teachers.map((teacher) => teacher.name).join(', ');
          return <Tooltip content={displayNamesShort}>{displayNamesShort}</Tooltip>;
        },
        accessorFn: (originalRow) => {
          const displayNamesShort = originalRow.teachers.map((teacher) => teacher.name);
          return displayNamesShort.join(', ');
        },
        size: 125,
      },
      {
        header: t('deputate.title', { count: 1 }),
        id: 'deputate.titleSingluar',
        cell: ({ row }) => {
          const lessonInfo = row.original.teachingLoadInfo;
          const relevantDeputate = teacherUuid
            ? lessonInfo?.deputatByTeacher.get(teacherUuid)
            : classUuid
              ? lessonInfo?.deputatByClass.get(classUuid)
              : (lessonInfo?.deputat ?? 0);
          return (relevantDeputate ?? 0).toFixed(2);
        },
        accessorFn: (originalRow) => {
          const lessonInfo = originalRow.teachingLoadInfo;
          const relevantDeputate = teacherUuid
            ? lessonInfo?.deputatByTeacher.get(teacherUuid)
            : classUuid
              ? lessonInfo?.deputatByClass.get(classUuid)
              : (lessonInfo?.deputat ?? 0);
          return (relevantDeputate ?? 0).toFixed(2);
        },
        size: 50,
      },
      {
        header: t('lesson.basics.isElective'),
        id: 'elective',
        accessorKey: 'elective',
        type: 'boolean',
      },
      {
        header: t('lesson.basics.isTeachingLoadEnabled.short'),
        meta: {
          filterName: t('lesson.basics.isTeachingLoadEnabled.full'),
          tooltip: t('lesson.basics.isTeachingLoadEnabled.full'),
        },
        id: 'teachingLoadEnabled',
        accessorKey: 'teachingLoadEnabled',
        type: 'boolean',
      },
      {
        header: t('lesson.basics.isTimetableEnabled.short'),
        meta: {
          filterName: t('lesson.basics.isTimetableEnabled.full'),
          tooltip: t('lesson.basics.isTimetableEnabled.full'),
        },
        id: 'timetableEnabled',
        accessorKey: 'timetableEnabled',
        type: 'boolean',
      },
      {
        header: t('common.editable.short'),
        id: 'editStatus',
        meta: {
          filterName: t('common.editable.full'),
          tooltip: t('common.editable.full'),
        },
        cell: ({ row }) => {
          const status = row.original.editStatus;
          // Server-seitig vorberechnet + lokalisiert: Gruende + betroffene Plaene/Versionen.
          const reasons = row.original.editStatusReasons ?? [];
          const statusChip = (
            <Chip
              value={
                row.original.editStatus === LessonEditStatus.Blocked ? (
                  <LockIcon color={getChipIconColor(status)} className={'smallest'} />
                ) : (
                  <UnlockIcon color={getChipIconColor(status)} className={'smallest'} />
                )
              }
              bgColor={getChipBgColor(status)}
            />
          );
          return (
            <>
              {status === LessonEditStatus.Unused || reasons.length === 0 ? (
                <>{statusChip}</>
              ) : (
                <Tooltip content={<EditStatusTooltipContent reasons={reasons} />}>{statusChip}</Tooltip>
              )}
            </>
          );
        },
        size: 48,
      },
    ];

    const dropdownItems = useCallback(
      (row: Row<LessonTableType>): DropdownMenuItem[] => {
        const qualified = allTeachers?.filter(
          (person) =>
            person.qualifications.some((q) => q.subjectUuid === row.original.subjectUuid) &&
            !row.original.teacherUuids.includes(person.uuid),
        );
        const teachers = qualified?.length
          ? qualified
          : allTeachers?.filter((person) => !row.original.teacherUuids.includes(person.uuid));

        if (lessonTabFilterType === 'subjectContainerLessons') {
          return [
            {
              label: t('epochs.editPlan'),
              onClick: () => {
                setLessonUuid(row.original.uuid);
                setEpochModalOpen(true);
              },
            },
            {
              type: 'ruler',
            },
            {
              label: t('common.delete'),
              type: 'default',
              color: 'error',
              disabled:
                row.original.editStatus === LessonEditStatus.PlacedCards ||
                row.original.editStatus === LessonEditStatus.Blocked,
              onClick: () => {
                handleDelete([row.original]);
              },
            },
          ];
        }

        const moreThanOneTeacherFromCurriculum =
          (allLessons.find((lesson) => lesson.uuid === row.original.uuid)?.item.curriculumPersons.length ?? 0) > 1;

        return [
          {
            label: t('lesson.edit'),
            onClick: () => {
              setLessonUuid(row.original.uuid);
              setEditModalOpen(true);
            },
          },
          {
            label: t('common.duplicate'),
            onClick: async () => {
              await handleDuplicate([row.original.uuid]);
            },
          },
          {
            label: t('lesson.actions.assignTo'),
            disabled: moreThanOneTeacherFromCurriculum || (row.original.teachers?.length ?? 0) > 1,
            subContent: teachers
              ? teachers.map((teacher) => {
                  return {
                    type: 'default',
                    label: teacher.displayNameShort ?? teacher.shortName ?? '',
                    onClick: () => {
                      handleAssign(row.original, [teacher]);
                    },
                  };
                })
              : [],
          },
          {
            label: t('lesson.actions.detach'),
            onClick: () => {
              handleDetach([row.original.uuid]);
            },
          },
          {
            type: 'ruler',
          },
          {
            label: t('lesson.actions.toggle.isElective'),
            type: 'switch',
            value: row.original.elective ?? false,
            onValueChange: async () => {
              await updateCheckboxes({ type: 'elective', uuids: [row.original.uuid], value: !row.original.elective });
            },
          },
          {
            label: t('lesson.basics.isTeachingLoadEnabled.full'),
            type: 'switch',
            value: row.original.teachingLoadEnabled ?? false,
            onValueChange: async () => {
              await updateCheckboxes({
                type: 'teachingLoadEnabled',
                uuids: [row.original.uuid],
                value: !row.original.teachingLoadEnabled,
              });
            },
          },
          {
            label: t('lesson.basics.isTimetableEnabled.full'),
            type: 'switch',
            value: row.original.timetableEnabled ?? false,
            disabled:
              row.original.editStatus === LessonEditStatus.PlacedCards ||
              row.original.editStatus === LessonEditStatus.InVersion ||
              row.original.editStatus === LessonEditStatus.Blocked,
            onValueChange: async () => {
              await updateCheckboxes({
                type: 'timetableEnabled',
                uuids: [row.original.uuid],
                value: !row.original.timetableEnabled,
              });
            },
          },
          {
            type: 'ruler',
          },
          {
            label: t('common.delete'),
            color: 'error',
            onClick: () => {
              handleDelete([row.original]);
            },
          },
        ];
      },
      [
        handleAssign,
        handleDelete,
        handleDetach,
        handleDuplicate,
        allLessons,
        allTeachers,
        updateCheckboxes,
        lessonTabFilterType,
        t,
      ],
    );

    const bulkEdit = useCallback(
      (selectedFlatRows: Row<LessonTableType>[]) => {
        const isSubjectContainerLesson = selectedFlatRows.every((s) => s.original.isSubjectContainerLesson);
        const dropDownData: DropdownMenuItem[] =
          isSubjectContainerLesson && permissionChecker?.isOmniAdmin()
            ? [
                {
                  label: t('lesson.actions.mergeSubjectContainerLessons'),
                  disabled: !isSubjectContainerLesson,
                  onClick: () => {
                    setLessonsToMerge(selectedFlatRows.map(({ original }) => original));
                    setMergeSubjectContainerLessonsModalOpen(true);
                  },
                },
              ]
            : [];

        dropDownData.push(
          ...[
            {
              label: t('lesson.basics.isElective'),
              disabled: isSubjectContainerLesson,
              subContent: [
                {
                  label: t('common.activateAll'),
                  disabled: isSubjectContainerLesson,
                  onClick: async () => {
                    await updateCheckboxes({
                      type: 'elective',
                      uuids: selectedFlatRows
                        .filter(({ original }) => !original.isSubjectContainerLesson)
                        .map(({ original }) => original.uuid),
                      value: true,
                    });
                    clearSelection();
                  },
                },
                {
                  label: t('common.deactivateAll'),
                  disabled: isSubjectContainerLesson,
                  onClick: async () => {
                    await updateCheckboxes({
                      type: 'elective',
                      uuids: selectedFlatRows
                        .filter(({ original }) => !original.isSubjectContainerLesson)
                        .map(({ original }) => original.uuid),
                      value: false,
                    });
                    clearSelection();
                  },
                },
              ],
            },
            {
              label: t('lesson.basics.isTimetableEnabled.full'),
              subContent: [
                {
                  label: t('common.activateAll'),
                  onClick: async () => {
                    await updateCheckboxes({
                      type: 'timetableEnabled',
                      uuids: selectedFlatRows
                        .filter(
                          ({ original }) =>
                            original.editStatus !== LessonEditStatus.InVersion &&
                            original.editStatus !== LessonEditStatus.PlacedCards,
                        )
                        .map(({ original }) => original.uuid),
                      value: true,
                    });
                    clearSelection();
                  },
                },
                {
                  label: t('common.deactivateAll'),
                  onClick: async () => {
                    await updateCheckboxes({
                      type: 'timetableEnabled',
                      uuids: selectedFlatRows
                        .filter(
                          ({ original }) =>
                            original.editStatus !== LessonEditStatus.InVersion &&
                            original.editStatus !== LessonEditStatus.PlacedCards,
                        )
                        .map(({ original }) => original.uuid),
                      value: false,
                    });
                    clearSelection();
                  },
                },
              ],
            },
            {
              label: t('lesson.basics.isTeachingLoadEnabled.full'),
              disabled: isSubjectContainerLesson,
              subContent: [
                {
                  label: t('common.activateAll'),
                  disabled: isSubjectContainerLesson,
                  onClick: async () => {
                    await updateCheckboxes({
                      type: 'teachingLoadEnabled',
                      uuids: selectedFlatRows
                        .filter(({ original }) => !original.isSubjectContainerLesson)
                        .map(({ original }) => original.uuid),
                      value: true,
                    });
                    clearSelection();
                  },
                },
                {
                  label: t('common.deactivateAll'),
                  disabled: isSubjectContainerLesson,
                  onClick: async () => {
                    await updateCheckboxes({
                      type: 'teachingLoadEnabled',
                      uuids: selectedFlatRows
                        .filter(({ original }) => !original.isSubjectContainerLesson)
                        .map(({ original }) => original.uuid),
                      value: false,
                    });
                    clearSelection();
                  },
                },
              ],
            },
          ],
        );

        const lessonPersonsSubmenuData: DropdownMenuItem[] =
          lessonTabFilterType === 'unassigned'
            ? []
            : [
                {
                  label: t('lesson.actions.detach'),
                  disabled: isSubjectContainerLesson,
                  onClick: async () => {
                    await handleDetach(
                      selectedFlatRows
                        .filter(({ original }) => !original.isSubjectContainerLesson)
                        .map(({ original }) => original.uuid),
                    );
                    clearSelection();
                  },
                },
              ];

        return (
          <DropdownMenu
            data={[
              ...dropDownData,
              ...(lessonPersonsSubmenuData.length > 0
                ? ([
                    { type: 'ruler' },
                    {
                      label: t('persons.title', { count: 2 }),
                      disabled: isSubjectContainerLesson,
                      subContent: lessonPersonsSubmenuData,
                    },
                  ] as DropdownMenuItem[])
                : []),
              { type: 'ruler' },
              {
                label: t('common.delete'),
                color: 'error',
                onClick: async () => {
                  await handleDelete(selectedFlatRows.map(({ original }) => original));
                  clearSelection();
                },
              },
            ]}
          />
        );
      },
      [
        clearSelection,
        handleDelete,
        handleDetach,
        t,
        updateCheckboxes,
        lessonTabFilterType,
        permissionChecker?.isOmniAdmin,
      ],
    );

    const lastCol = useCallback(
      (row: Row<LessonTableType>) => {
        if (row.original.editStatus === LessonEditStatus.Blocked) {
          return (
            <Button
              hierarchy={'secondary'}
              icon={<VisibleIcon className='small' />}
              onClick={() => {
                setLessonUuid(row.original.uuid);
                if (lessonTabFilterType === 'subjectContainerLessons') {
                  setEpochModalOpen(true);
                } else {
                  setEditModalOpen(true);
                }
              }}
            ></Button>
          );
        }

        return (
          <Dropdown
            disabled={readonly}
            trigger={
              <Button
                isLoading={loadingState.some(({ loading, uuid }) => uuid === row.original.uuid && loading)}
                hierarchy='secondary'
                icon={<DotsHorizontalIcon className='small' />}
                disabled={readonly}
              />
            }
          >
            <DropdownMenu maxHeight='s' data={dropdownItems(row)} />
          </Dropdown>
        );
      },
      [dropdownItems, loadingState, lessonTabFilterType, readonly],
    );

    // "Lehrende zuweisen" (Bulk): arbeitet auf den markierten Zeilen. rowSelection ist nach dem
    // Default-getRowId von react-table auf den Index in lessonsData gekeyt.
    const selectedLessons = useMemo(
      () =>
        Object.entries(rowSelection)
          .filter(([, selected]) => selected)
          .map(([key]) => lessonsData[Number(key)])
          .filter((lesson): lesson is LessonTableType => Boolean(lesson)),
      [rowSelection, lessonsData],
    );

    // Nur aktiv, wenn ausschließlich offene Zuordnungen (ohne Lehrende) markiert sind. Grau bei
    // keiner Auswahl oder sobald eine bereits zugewiesene Zuordnung mit ausgewählt ist.
    const assignTeachersDisabled =
      selectedLessons.length === 0 || selectedLessons.some((lesson) => lesson.teachers.length > 0);

    // "Mit Lehrbefähigung": Lehrkräfte, die für ALLE ausgewählten Fächer qualifiziert sind; der Rest
    // landet unter "Ohne Lehrbefähigung".
    const selectedSubjectUuids = useMemo(
      () => [...new Set(selectedLessons.map((lesson) => lesson.subjectUuid))],
      [selectedLessons],
    );
    const [qualifiedTeachers, unqualifiedTeachers] = useMemo(
      () =>
        partition(
          allTeachers ?? [],
          (person) =>
            selectedSubjectUuids.length > 0 &&
            selectedSubjectUuids.every((subjectUuid) =>
              person.qualifications.some((q) => q.subjectUuid === subjectUuid),
            ),
        ),
      [allTeachers, selectedSubjectUuids],
    );

    const handleBulkAssignTeacher = useCallback(
      async (teacher: { uuid: string }) => {
        await Promise.all(selectedLessons.map((lesson) => handleAssign(lesson, [teacher])));
        clearSelection();
      },
      [selectedLessons, handleAssign, clearSelection],
    );

    const handleBulkInherit = useCallback(
      async (type: InheritLessonTeachersType) => {
        await handleInheritLessonTeachers(
          selectedLessons.map((lesson) => lesson.uuid),
          type,
        );
        clearSelection();
      },
      [selectedLessons, handleInheritLessonTeachers, clearSelection],
    );

    const teacherMenuItem = (teacher: {
      uuid: string;
      displayNameShort?: string | null;
      shortName?: string | null;
    }): DropdownMenuItem => ({
      label: teacher.displayNameShort ?? teacher.shortName ?? '',
      onClick: () => handleBulkAssignTeacher(teacher),
    });

    const actionBarSettings = {
      showSelectedRowsCount: true,
      showExpertFilter: true,
      showPrintButton: true,
      showBulkEdit: true,
      disableBulkEdit: readonly,
      showAddButton: false,
      extendedActionsRight: (
        <>
          {showTransferCurricula && (
            <Button
              hierarchy='tertiary'
              icon={<ImportIcon />}
              onClick={() => setTransferModalOpen(true)}
              disabled={readonly}
            >
              {t('curriculum.transferCurriculum')}
            </Button>
          )}
          {lessonTabFilterType === 'subjectContainerLessons' ? (
            <Button
              hierarchy='tertiary'
              icon={<AddIcon />}
              onClick={handleAddSubjectContainerLesson}
              disabled={readonly}
            >
              {t('common.add')}
            </Button>
          ) : (
            <Dropdown
              disabled={readonly}
              trigger={
                <Button hierarchy='tertiary' icon={<AddIcon />} disabled={readonly}>
                  {t('common.add')}
                </Button>
              }
            >
              <DropdownMenu
                maxHeight='m'
                data={[
                  {
                    label: t('lesson.actions.transferCurriculum'),
                    onClick: () => setTransferModalOpen(true),
                  },
                  {
                    label: t('lesson.actions.manualLesson'),
                    onClick: handleAddLesson,
                  },
                ]}
              />
            </Dropdown>
          )}
          <Dropdown
            disabled={readonly || assignTeachersDisabled}
            trigger={
              <Button icon={<GroupsIcon />} hierarchy='tertiary' disabled={readonly || assignTeachersDisabled}>
                {t('lesson.actions.assignTeachers')}
              </Button>
            }
          >
            <DropdownMenu
              maxHeight='m'
              data={[
                // Nur 2 Menüebenen: eine 3. Ebene würde bei wenig Platz das 1. Menü überdecken
                // (Radix positioniert Submenüs nur gegen den Viewport-Rand, nicht gegen Geschwister).
                {
                  label: t('lesson.actions.fromPreviousYear'),
                  subContent: [
                    {
                      label: t('lesson.actions.inheritAllSameClassLevel'),
                      onClick: () => handleBulkInherit(InheritLessonTeachersType.SameClassLevel),
                    },
                    {
                      label: t('lesson.actions.inheritAllSameStudents'),
                      onClick: () => handleBulkInherit(InheritLessonTeachersType.SameStudents),
                    },
                  ],
                },
                {
                  label: t('qualifications.withQualification'),
                  disabled: qualifiedTeachers.length === 0,
                  subContent: qualifiedTeachers.map(teacherMenuItem),
                },
                {
                  label: t('qualifications.withoutQualification'),
                  disabled: unqualifiedTeachers.length === 0,
                  subContent: unqualifiedTeachers.map(teacherMenuItem),
                },
              ]}
            />
          </Dropdown>
        </>
      ),
    };

    const getLessonCards = (lesson: LessonTableType) => {
      const cardInfos = lesson.cardsInfos;
      return (
        <div className={style['card-position']}>
          {cardInfos?.defaultCards.map((group) => {
            const card = group[0];
            return (
              <div key={card.uuid} title={card.cardLabel}>
                <LessonCard
                  label={card.cardLabelShort}
                  lessonClasses={card.lesson}
                  count={card.counter}
                  teacherColors={card.colors}
                  duration={card.duration}
                  showHover={false}
                />
              </div>
            );
          })}
        </div>
      );
    };

    return (
      <>
        <Table<LessonTableType>
          showBorderRadius
          showShadow
          // mobil nicht: der Kopf klebt per position: fixed und liefe beim seitlichen Wischen nicht mit
          stickyHeader={!isMobile}
          stickyHeaderOptions={{
            top: 60, // height: var(--header-height)
          }}
          virtualize={true}
          virtualizerOptions={{
            getScrollElement: () => {
              return scrollRef?.current ?? null;
            },
            measureElement: (element) => {
              return element.getBoundingClientRect().height;
            },
            estimateSize: (row) => estimatedRowHeightByCards(row.original.cardsInfos),
          }}
          showSelected
          showVisibility
          loading={loading || loadingState?.some(({ uuid, loading }) => uuid === 'all' && loading)}
          isOnWhite={false}
          printerSettings={{
            headline: pimAuthClaims.getProfile()?.organization.name,
            subline: `${t('lesson.title', { count: 2 })} - ${t('common.schoolYear')} ${schoolYear?.shortName}`,
            filename: `${t('lesson.title', { count: 2 })}_${schoolYear?.shortName}`,
          }}
          showActionBar
          rowSelection={rowSelection}
          onRowSelectionChange={onRowSelectionChange}
          showSelect
          showSort
          columnVisibility={columnVisibility}
          onColumnVisibilityChange={saveColumnVisibility}
          sorting={sorting}
          onSortingChange={saveSorting}
          bulkEditDropdownContent={bulkEdit}
          actionBarSettings={actionBarSettings}
          emptyStateSettings={
            showTransferCurricula
              ? {
                  showButton: !readonly,
                  buttonIcon: <ImportIcon />,
                  buttonText: t('curriculum.transferCurriculum'),
                  onButtonClick: () => setTransferModalOpen(true),
                }
              : undefined
          }
          columns={columns}
          data={lessonsData}
          lastCol={lastCol}
        />

        <DeputateLessonModal
          lessonUuid={lessonUuid}
          isOpen={editModalOpen}
          onClose={handleClose}
          defaultInitialValues={defaultInitialValues}
        />
        <DeputateEpochModal
          lessonUuid={lessonUuid}
          isOpen={epochModalOpen}
          onClose={handleAddEpochClose}
          classUuid={classUuid}
        />
        <MergeSubjectContainerLessonsModal
          open={mergeSubjectContainerLessonsModalOpen}
          onClose={() => {
            setMergeSubjectContainerLessonsModalOpen(false);
          }}
          handleMerge={async ({ cardsFromLesson, lessonUuids, newCards }) => {
            setLoading(true);
            await mergeLessons(
              {
                lessonUuids: lessonUuids,
                cardsFromLesson: cardsFromLesson,
                newCards: newCards ?? [],
              },
              context,
            );
            setLoading(false);
            setMergeSubjectContainerLessonsModalOpen(false);
            setLessonsToMerge([]);
          }}
          lessons={lessonsToMerge.map((lesson) => {
            return {
              uuid: lesson.uuid,
              name: `${lesson.subject} - ${lesson.groupClassNames}`,
              subjectContainerUuid: lesson.subjectUuid,
              subjectName: lesson.subject,
              onlyInTimetableVersion: false,
              cards: getLessonCards(lesson),
            };
          })}
        />
        <TransferCurriculaModal isOpen={transferModalOpen} onClose={() => setTransferModalOpen(false)} />
        <ConfirmationDialog />
      </>
    );
  },
);
